import React, { useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import {
  ShoppingCart, Check, ChevronLeft, FileText, Snowflake, Package,
  ArrowUpRight, ShieldCheck, FlaskConical, Loader2, Beaker,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import SEO from '../components/SEO';
import FreeShippingProgress from '../components/FreeShippingProgress';
import { trackViewItem } from '../lib/analytics';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Bulk pricing tiers — stay in lockstep with backend bulk_pricing.py
const TIERS = [
  { min: 4,  max: 6,  pct: 5  },
  { min: 7,  max: 9,  pct: 10 },
  { min: 10, max: Infinity, pct: 15 },
];

export default function ProductDetailPage() {
  const { id: routeId } = useParams();
  const id = routeId || 'glp3-rt';
  const { addToCart, cart } = useCart();
  const { user } = useAuth();

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [variant, setVariant] = useState(null);
  const [qty, setQty] = useState(1);
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/products/${id}`)
      .then((r) => {
        setProduct(r.data);
        if (r.data.variants?.length) setVariant(r.data.variants[0].label);
      })
      .catch(() => setProduct(null))
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    if (product?.id) trackViewItem({ id: product.id, name: product.name, price: product.price });
  }, [product?.id, product?.name, product?.price]);

  const isPeptide = !!product?.product_types?.includes('peptide');
  // The "Tip" product is classified into peptide+equipment so it appears
  // under both catalog nav filters, but it shouldn't display peptide-specific
  // chrome (HPLC/pre-order/RUO disclaimer/bulk pricing). One flag handles it.
  const isTip = product?.id === 'tip';
  const currentVariant = product && variant && product.variants
    ? product.variants.find((v) => v.label === variant) : null;
  const currentPrice = currentVariant?.price || product?.price || 0;
  const variantStock = currentVariant?.stock ?? product?.stock ?? 0;

  // Memoise the profile object so its identity doesn't change on every
  // render — `product?.molecular_profile || {}` allocates a fresh `{}` each
  // call, defeating the useMemo on `profileFields` below.
  const profile = useMemo(() => product?.molecular_profile || {}, [product]);
  const refs = product?.references || [];

  const profileFields = useMemo(() => ([
    { label: 'Common Name',         value: profile.common_name },
    { label: 'Molecular Formula',   value: profile.molecular_formula },
    { label: 'Molecular Weight',    value: profile.molecular_weight },
    { label: 'CAS Number',          value: profile.cas_number },
    { label: 'IUPAC / Description', value: profile.iupac_name },
    { label: 'Sequence Class',      value: profile.sequence_class },
  ].filter((f) => f.value && f.value !== '—')), [profile]);

  const activeTier = TIERS.find((t) => qty >= t.min && qty <= t.max);
  const discountedUnit = activeTier ? currentPrice * (1 - activeTier.pct / 100) : currentPrice;
  const lineTotal = discountedUnit * qty;

  const jsonLd = useMemo(() => {
    if (!product) return null;
    const offers = (product.variants?.length ? product.variants : [{ label: product.weight, price: product.price }])
      .map((v) => ({ '@type': 'Offer', name: v.label, price: Number(v.price).toFixed(2), priceCurrency: 'USD',
        availability: (v.stock ?? product.stock ?? 1) > 0 ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock',
        url: `${typeof window !== 'undefined' ? window.location.origin : ''}/product/${product.id}` }));
    return {
      '@context': 'https://schema.org/', '@type': 'Product',
      name: product.id === 'glp3-rt' ? 'Retatrutide (GLP3-RT)' : product.name,
      description: (product.description || '').slice(0, 300),
      image: product.image ? [product.image] : [],
      sku: product.id, brand: { '@type': 'Brand', name: 'Wilvada Peptide Solutions' },
      offers: offers.length === 1 ? offers[0] : offers,
    };
  }, [product]);

  const handleAddToCart = async () => {
    if (!user) return toast.error('Please sign in to add items to cart');
    setAdding(true);
    const r = await addToCart(product.id, qty, variant);
    if (r.success) toast.success(`${product.name} added to cart`);
    else toast.error(r.error || 'Failed to add');
    setTimeout(() => setAdding(false), 900);
  };

  if (loading) return (
    <div className="max-w-7xl mx-auto px-4 py-24 grid lg:grid-cols-12 gap-12">
      <div className="lg:col-span-6"><div className="aspect-square rounded-2xl bg-slate-100 animate-pulse" /></div>
      <div className="lg:col-span-6 space-y-4"><div className="h-6 w-1/3 bg-slate-100 animate-pulse rounded" /><div className="h-12 w-2/3 bg-slate-100 animate-pulse rounded" /><div className="h-32 bg-slate-100 animate-pulse rounded" /></div>
    </div>
  );
  if (!product) return (
    <div className="max-w-7xl mx-auto px-4 py-24 text-center">
      <p className="text-lg text-slate-700 font-heading mb-4">Product not found</p>
      <Button asChild variant="outline"><Link to="/catalog">Back to Catalog</Link></Button>
    </div>
  );

  return (
    <div className="bg-white text-slate-900" data-testid="test-product-page">
      <SEO
        title={product.id === 'glp3-rt' ? 'Retatrutide (GLP3-RT) — GLP-1 / GIP / Glucagon Triple Agonist' : `${product.name} — Research Peptide`}
        description={(product.description || '').slice(0, 280)}
        image={product.image}
        jsonLd={jsonLd}
      />

      {/* Breadcrumb */}
      <nav className="border-b border-slate-200 bg-white" data-testid="pdp-breadcrumb">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 text-xs text-slate-500 font-body flex items-center gap-1.5">
          <Link to="/" className="hover:text-slate-700">Wilvada</Link>
          <span>/</span>
          <Link to="/catalog?product_type=peptide" className="hover:text-slate-700">Peptides</Link>
          <span>/</span>
          <span className="text-slate-900 font-medium">{product.name}</span>
        </div>
      </nav>

      {/* ─── PRODUCT GRID ────────────────────────────── */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-16 grid lg:grid-cols-12 gap-12 lg:gap-16" data-testid="pdp-container">
        {/* IMAGE GALLERY */}
        <aside className="lg:col-span-6 lg:sticky lg:top-32 h-fit" data-testid="pdp-gallery">
          <Button asChild variant="ghost" size="sm" className="mb-4 -ml-3 text-slate-600 hover:text-slate-900">
            <Link to="/catalog"><ChevronLeft className="h-4 w-4 mr-1" /> Back to Catalog</Link>
          </Button>
          <div className="aspect-square rounded-2xl bg-slate-50 border border-slate-200 overflow-hidden flex items-center justify-center p-10 relative" data-testid="pdp-image">
            <img src={product.image} alt={product.name} className="w-full h-full object-contain mix-blend-multiply" />
            {profile.pubchem_cid && (
              <div className="absolute top-4 right-4 bg-white border border-slate-200 rounded-full px-3 py-1 text-[10px] uppercase tracking-[0.15em] text-slate-600 font-body" data-testid="pdp-pubchem-badge">
                PubChem CID {profile.pubchem_cid}
              </div>
            )}
          </div>
          <p className="mt-4 text-[11px] text-slate-500 font-body italic leading-relaxed">
            Stock images are illustrative. The vial you receive ships in temperature-controlled packaging with a Certificate of Analysis.
          </p>
        </aside>

        {/* INFO COLUMN */}
        <main className="lg:col-span-6 flex flex-col gap-8" data-testid="pdp-info">
          <header data-testid="pdp-header">
            <p className="text-[11px] tracking-[0.2em] uppercase font-bold text-[hsl(210,45%,40%)] font-body">{product.subtitle || (isPeptide ? 'Research Peptide' : 'Lab Equipment')}</p>
            <h1 className="font-heading text-4xl lg:text-5xl tracking-tight font-light text-slate-900 mt-2 leading-[1.1]">
              {product.id === 'glp3-rt' ? <>Retatrutide <span className="text-slate-400 font-light">/</span> <span className="text-slate-500 text-3xl">GLP3-RT</span></> : product.name}
            </h1>
            <div className="mt-5 flex items-baseline gap-3 flex-wrap">
              <span className="font-heading text-3xl font-medium text-slate-900" data-testid="pdp-price">${currentPrice.toFixed(2)}</span>
              <span className="text-sm text-slate-500 font-body">/ {variant || product.weight}</span>
              <span className={`ml-2 text-xs font-medium px-2 py-0.5 rounded-full ${variantStock > 0 ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-rose-50 text-rose-700 border border-rose-200'}`} data-testid="pdp-stock">
                {variantStock > 0 ? `${variantStock} in stock` : 'Out of stock'}
              </span>
              {!isTip && (
                <span className="inline-flex items-center gap-1 text-[10px] font-bold tracking-[0.15em] uppercase px-2 py-0.5 rounded-full bg-amber-100 text-amber-900 border border-amber-200" data-testid="pdp-preorder-pill">
                  Pre-Order · Ships ~July 2026
                </span>
              )}
            </div>
            {!isTip && (
              <p className="mt-2 text-xs text-slate-500 font-body" data-testid="pdp-preorder-note">
                Wave 01 closes <span className="font-semibold text-slate-700">June 30, 2026</span>. Your card is charged at checkout to reserve your batch; COAs upload to your dashboard before this item ships.{' '}
                <Link to="/pre-orders" className="text-[hsl(210,45%,40%)] hover:underline font-medium">Read pre-order policy</Link>
              </p>
            )}
          </header>

          {/* Trust badges row */}
          {!isTip && (
          <div className="flex flex-wrap gap-2" data-testid="pdp-trust-badges">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full border bg-slate-50 border-slate-200 text-xs font-medium text-slate-700">
              <ShieldCheck className="h-3.5 w-3.5 text-emerald-600" /> HPLC ≥{product.purity || 99}% Purity
            </span>
            {product.coa_image && (
              <a href={product.coa_image} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full border bg-emerald-50 border-emerald-200 text-xs font-medium text-emerald-800 hover:bg-emerald-100 transition-colors" data-testid="coa-pdf-link">
                <FileText className="h-3.5 w-3.5" /> View Certificate of Analysis <ArrowUpRight className="h-3 w-3" />
              </a>
            )}
          </div>
          )}

          {/* Variant + Qty + CTA */}
          <div className="space-y-4" data-testid="pdp-purchase-block">
            {product.variants?.length > 1 && (
              <div>
                <label className="block text-[11px] uppercase tracking-[0.15em] font-semibold text-slate-500 mb-2 font-body">Size</label>
                <Select value={variant} onValueChange={setVariant}>
                  <SelectTrigger className="w-full sm:w-64 border-slate-300" data-testid="variant-select-dropdown">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {product.variants.map((v) => (
                      <SelectItem key={v.label} value={v.label}>
                        {v.label} — ${v.price.toFixed(2)} {v.stock <= 0 ? '(out)' : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="flex items-center gap-3">
              <div className="inline-flex items-center border border-slate-300 rounded-md h-12 w-32 overflow-hidden" data-testid="quantity-stepper">
                <button onClick={() => setQty((q) => Math.max(1, q - 1))} className="h-full w-10 text-slate-600 hover:bg-slate-50" data-testid="quantity-stepper-decrease">−</button>
                <span className="flex-1 text-center text-sm font-medium tabular-nums" data-testid="pdp-quantity">{qty}</span>
                <button onClick={() => setQty((q) => q + 1)} className="h-full w-10 text-slate-600 hover:bg-slate-50" data-testid="quantity-stepper-increase">+</button>
              </div>
              <Button
                onClick={handleAddToCart}
                disabled={adding || variantStock <= 0}
                className="flex-1 h-12 bg-[hsl(210,52%,21%)] hover:bg-slate-800 text-white font-medium rounded-md text-sm gap-2"
                data-testid="add-to-cart-button"
              >
                {adding ? <Check className="h-4 w-4" /> : <ShoppingCart className="h-4 w-4" />}
                {adding ? 'Added to cart' : variantStock <= 0 ? 'Out of stock' : isTip ? `Add Tip · $${lineTotal.toFixed(2)}` : `Reserve Pre-Order · $${lineTotal.toFixed(2)}`}
              </Button>
            </div>
            <FreeShippingProgress subtotal={(cart?.total || 0) + (variantStock > 0 ? lineTotal : 0)} hidden={isTip} />
          </div>

          {/* Bulk pricing tiers */}
          {isPeptide && !isTip && (
            <div className="rounded-xl bg-slate-50 border border-slate-200 p-5" data-testid="pdp-bulk-tiers">
              <p className="text-[11px] uppercase tracking-[0.15em] font-bold text-emerald-700 font-body mb-3 inline-flex items-center gap-1.5">
                <Beaker className="h-3.5 w-3.5" /> Bulk Pricing
              </p>
              <div className="grid grid-cols-3 gap-2 text-center">
                {TIERS.map((t) => {
                  const active = activeTier && activeTier.min === t.min;
                  return (
                    <div key={t.min} className={`rounded-lg px-2 py-3 border transition-all ${active ? 'bg-emerald-100 border-emerald-300 ring-2 ring-emerald-300' : 'bg-white border-slate-200'}`} data-testid={`bulk-tier-${t.min}`}>
                      <p className="text-[10px] uppercase tracking-[0.12em] font-semibold text-slate-500 font-body">{t.max === Infinity ? `${t.min}+ vials` : `${t.min}–${t.max} vials`}</p>
                      <p className={`text-lg font-heading font-medium mt-0.5 ${active ? 'text-emerald-800' : 'text-slate-900'}`}>{t.pct}% off</p>
                    </div>
                  );
                })}
              </div>
              {activeTier && (
                <p className="mt-3 text-xs text-emerald-700 font-body" data-testid="pdp-bulk-active">
                  ✓ At {qty} vials you save <strong>{activeTier.pct}%</strong> — line total <strong>${lineTotal.toFixed(2)}</strong> (you save ${(currentPrice * qty - lineTotal).toFixed(2)}).
                </p>
              )}
            </div>
          )}

          {/* Description */}
          <section className="border-t border-slate-200 pt-8" data-testid="pdp-description">
            <h2 className="font-heading text-xl font-medium text-slate-900 mb-3">{isTip ? 'About this gratuity' : 'About this peptide'}</h2>
            <p className="text-sm text-slate-600 leading-relaxed font-body whitespace-pre-line">{product.description}</p>
          </section>

          {/* Molecular profile */}
          {isPeptide && profileFields.length > 0 && (
            <section className="border-t border-slate-200 pt-8" data-testid="molecular-profile-section">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-heading text-xl font-medium text-slate-900 inline-flex items-center gap-2"><FlaskConical className="h-5 w-5 text-[hsl(210,45%,40%)]" /> Molecular Profile</h2>
                {profile.pubchem_url && (
                  <a href={profile.pubchem_url} target="_blank" rel="noopener noreferrer" className="text-xs text-[hsl(210,45%,40%)] hover:underline inline-flex items-center gap-1" data-testid="pubchem-link">
                    PubChem <ArrowUpRight className="h-3 w-3" />
                  </a>
                )}
              </div>
              <dl className="grid grid-cols-2 gap-x-8 gap-y-4">
                {profileFields.map((f) => (
                  <div key={f.label}>
                    <dt className="text-[10px] uppercase tracking-[0.15em] font-semibold text-slate-500 font-body">{f.label}</dt>
                    <dd className="text-sm text-slate-900 font-medium mt-1 break-words font-body">{f.value}</dd>
                  </div>
                ))}
              </dl>
              {(product.storage || product.form) && (
                <div className="grid sm:grid-cols-2 gap-3 mt-6">
                  {product.storage && (
                    <div className="flex items-start gap-3 p-3 rounded-md bg-white border border-slate-200" data-testid="product-storage">
                      <Snowflake className="h-5 w-5 text-blue-500 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-[10px] uppercase tracking-[0.12em] font-semibold text-slate-500 font-body">Storage</p>
                        <p className="text-sm text-slate-900 mt-0.5 font-body">{product.storage}</p>
                      </div>
                    </div>
                  )}
                  {product.form && (
                    <div className="flex items-start gap-3 p-3 rounded-md bg-white border border-slate-200" data-testid="product-form-info">
                      <Package className="h-5 w-5 text-slate-500 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-[10px] uppercase tracking-[0.12em] font-semibold text-slate-500 font-body">Form</p>
                        <p className="text-sm text-slate-900 mt-0.5 font-body">{product.form}</p>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </section>
          )}

          {/* Reference Library */}
          {isPeptide && refs.length > 0 && (
            <section className="border-t border-slate-200 pt-8" data-testid="pdp-references-section">
              <h2 className="font-heading text-xl font-medium text-slate-900 mb-2 inline-flex items-center gap-2"><FileText className="h-5 w-5 text-[hsl(210,45%,40%)]" /> Reference Library</h2>
              <p className="text-xs text-slate-500 font-body mb-4">Curated public-database sources. Educational only — not medical advice.</p>
              <ul className="space-y-2">
                {refs.map((r, i) => (
                  <li key={`${r.url || r.source || 'ref'}-${i}`}>
                    <a href={r.url} target="_blank" rel="noopener noreferrer nofollow" className="block group rounded-md border border-slate-200 hover:border-[hsl(210,45%,40%)]/50 hover:bg-slate-50 transition-colors p-3" data-testid={`pdp-reference-link-${i}`}>
                      <div className="flex items-start gap-3">
                        <span className="font-heading text-xs text-slate-400 font-medium tabular-nums mt-0.5">[{i + 1}]</span>
                        <div className="flex-1 min-w-0">
                          <p className="text-[10px] uppercase tracking-[0.12em] font-semibold text-[hsl(210,45%,40%)] font-body">{r.source}</p>
                          <p className="text-sm font-medium text-slate-900 mt-0.5 leading-snug">{r.title} <ArrowUpRight className="inline h-3 w-3 opacity-50 group-hover:opacity-100" /></p>
                          {r.description && <p className="text-xs text-slate-500 mt-1 leading-snug font-body">{r.description}</p>}
                        </div>
                      </div>
                    </a>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {/* FDA / RUO disclaimer */}
          {!isTip && (
          <section className="border-t border-slate-200 pt-8" data-testid="fda-disclaimer">
            <div className="rounded-md bg-slate-50 border border-slate-200 p-4">
              <p className="text-[10px] uppercase tracking-[0.15em] font-bold text-slate-700 font-body">For Research Use Only</p>
              <p className="text-xs text-slate-600 mt-2 leading-relaxed font-body">
                Not for human or veterinary consumption. By purchasing, you confirm this product will be used exclusively for in-vitro and pre-clinical laboratory research.
                These statements have not been evaluated by the Food and Drug Administration. This product is not intended to diagnose, treat, cure, or prevent any disease.
                Wilvada makes no claims regarding the safety, efficacy, or therapeutic benefit of this compound for use in humans or animals.
              </p>
            </div>
          </section>
          )}
        </main>
      </div>
    </div>
  );
}
